import {
  allVisionConditions,
  Application,
  ApplicationFormData,
  useApplication,
  useUpdateApplication,
} from "@/api/application/application";
import { DangerButton } from "@sciencecorp/helix-components";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Link,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { InputTextArea } from "@/components/UI/forms/InputTextArea";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { MdOutlineCheckCircleOutline, MdOutlineCircle } from "react-icons/md";
import { stepCompletion } from "../validation";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "@/components/i18n/LocalizedLink";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";

// Keep in sync with the additional_info length validation on Registry::Application.
const ADDITIONAL_INFO_MAX_LENGTH = 2000;

export const ConfirmPage = () => {
  const { data: applicationData, isLoading } = useApplication();
  const { mutateAsync: updateApplication } = useUpdateApplication();
  const toast = useToast();
  const localizedNavigate = useLocalizedNavigate();
  const { t } = useTranslation(["registry", "common"]);

  const {
    general: generalComplete,
    consent: consentComplete,
    medical: medicalComplete,
    submitted: applicationSubmitted,
  } = useMemo(() => {
    return applicationData
      ? stepCompletion(applicationData)
      : { general: false, consent: false, medical: false, submitted: false };
  }, [applicationData]);

  const steps = useMemo(
    () => [
      {
        name: t("registry:confirm.generalInfo"),
        complete: generalComplete,
        to: "/registry",
      },
      {
        name: t("registry:confirm.noticeConsent"),
        complete: consentComplete,
        to: "/registry/consent",
      },
      {
        name: t("registry:confirm.medicalInfo"),
        complete: medicalComplete,
        to: "/registry/medical",
      },
    ],
    [generalComplete, consentComplete, medicalComplete, t],
  );

  const canSubmit = generalComplete && consentComplete && medicalComplete;
  const isSubmitted = !!applicationData?.is_submitted;

  // Null until the applicant types, so the saved value shows through once the
  // application query resolves without needing an effect to seed state. Only
  // persisted on submit — like a normal form, not saved as they type.
  const [additionalInfoDraft, setAdditionalInfoDraft] = useState<string | null>(null);
  const additionalInfo = additionalInfoDraft ?? applicationData?.additional_info ?? "";

  // PRIMA eligibility check based on form answers
  const primaEligible = useMemo(() => {
    const app = applicationData;
    if (!app) return false;

    // Must have AMD
    const hasAMD = app.vision_conditions?.includes(
      allVisionConditions.AGE_RELATED_MACULAR_DEGENERATION,
    );
    if (!hasAMD) return false;

    // AMD type must not be "wet"
    if (app.vision_macular_degeneration_type === "wet") return false;

    // If visual acuity is known, check thresholds by country
    if (app.vision_acuity_known === true) {
      const left = parseFloat(app.vision_acuity_left || "");
      const right = parseFloat(app.vision_acuity_right || "");

      // Only check acuity thresholds if both values are valid numbers
      if (!isNaN(left) && !isNaN(right)) {
        const patientCountry =
          (app.applicant_type === "proxy" ? app.proxy_country : app.patient_country) || "US";

        // Check visual acuity thresholds based on country
        // Requirements: worse eye threshold AND better eye threshold
        let meetsAcuityThreshold = false;

        switch (patientCountry) {
          case "DE": {
            // DE: 6% or less in one eye, and 10% or less in the other (lower = worse)
            const worseEye = Math.min(left, right);
            const betterEye = Math.max(left, right);
            meetsAcuityThreshold = worseEye <= 6 && betterEye <= 10;
            break;
          }
          case "FR": {
            // FR: 0.06 or less in one eye, and 0.10 or less in the other (lower = worse)
            const worseEye = Math.min(left, right);
            const betterEye = Math.max(left, right);
            meetsAcuityThreshold = worseEye <= 0.06 && betterEye <= 0.1;
            break;
          }
          case "GB":
          case "UK": {
            // UK: 6/96 or worse in one eye, and 6/60 or worse in the other (higher = worse)
            const worseEye = Math.max(left, right);
            const betterEye = Math.min(left, right);
            meetsAcuityThreshold = worseEye >= 96 && betterEye >= 60;
            break;
          }
          default: {
            // US: 20/320 or worse in one eye, and 20/200 or worse in the other (higher = worse)
            const worseEye = Math.max(left, right);
            const betterEye = Math.min(left, right);
            meetsAcuityThreshold = worseEye >= 320 && betterEye >= 200;
            break;
          }
        }

        if (!meetsAcuityThreshold) return false;
      }
    }

    // If visual acuity unknown, check functional vision questions
    if (app.vision_acuity_known === false) {
      // If they can read normal print, vision is too good
      if (app.vision_can_read_normal_print === true) return false;

      // If they can read headlines, vision is too good
      if (app.vision_can_read_headlines === true) return false;
    }

    // Should not be driving (with or without restrictions)
    if (app.vision_driving === "yes" || app.vision_driving === "yes_with_limitations") return false;

    return true;
  }, [applicationData]);

  const submit = useCallback(async () => {
    if (!applicationData?.id) return;
    if (!canSubmit) return;

    const payload: ApplicationFormData = {
      ...applicationData,
      additional_info: additionalInfo.trim() || null,
      is_submitted: true,
    };

    try {
      await updateApplication({ id: applicationData.id, ...payload });

      // Fire Plausible analytics event based on eligibility
      const plausible = (window as { plausible?: (event: string) => void }).plausible;
      if (typeof plausible === "function") {
        plausible(primaEligible ? "registration_qual" : "registration_un");
      }

      toast({
        title: t("registry:confirm.applicationSubmitted"),
        description: t("registry:confirm.submittedToast"),
        status: "success",
        duration: 10000,
        isClosable: true,
      });
      localizedNavigate("/");
      window.scrollTo(0, 0);
    } catch (error) {
      const message = error instanceof Error ? error.message : t("common:errors.unknownError");
      toast({
        title: t("common:toast.error"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    applicationData,
    additionalInfo,
    canSubmit,
    localizedNavigate,
    toast,
    updateApplication,
    primaEligible,
    t,
  ]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <VStack spacing={6} alignItems="stretch">
      <Flex fontSize="md" alignItems="baseline" gap={1} color="gray.600" fontWeight="semibold">
        <Link as={LocalizedLink} color="blue.500" to="/">
          &larr; {t("common:buttons.backToStepList")}
        </Link>
      </Flex>

      <VStack
        paddingBlock={[0, 4, 8]}
        spacing={6}
        maxWidth="container.md"
        margin="auto"
        alignItems="flex-start"
        marginBlockEnd={8}
      >
        <Heading fontSize={["2xl", "3xl", "4xl"]} fontWeight="regular">
          <Text
            fontSize="md"
            as="div"
            textTransform="uppercase"
            color="gray.600"
            fontWeight="semibold"
          >
            {t("registry:confirm.stepLabel")}
          </Text>
          {t("registry:confirm.heading")}
        </Heading>

        <Stack fontSize="lg" width="full" spacing={4}>
          {steps.slice(0, 3).map((step) => (
            <Flex
              key={step.name}
              alignItems="center"
              width="full"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              justifyContent="flex-start"
              flexWrap={["wrap", "nowrap"]}
              backgroundColor="white"
            >
              <Icon
                height={16}
                width={8}
                flexBasis={8}
                flexGrow={0}
                paddingBlock={5}
                marginInlineStart={[4, 6]}
                as={step.complete ? MdOutlineCheckCircleOutline : MdOutlineCircle}
                color={step.complete ? "blue.500" : "gray.400"}
              />
              <Flex
                paddingInline={[2, 6]}
                paddingBlock={[4, 6]}
                gap={4}
                width={["auto", "auto", "full"]}
                direction={["column", "column", "row"]}
                alignItems="center"
              >
                <Box flexBasis={["80%", "auto"]}>
                  <Heading fontSize={["xl", "2xl"]} fontWeight="medium">
                    {step.name}
                  </Heading>
                  <Text fontSize={["md", "lg"]}>
                    {step.complete ? t("common:status.complete") : t("common:status.incomplete")}
                  </Text>
                </Box>
                <Button
                  as={LocalizedLink}
                  to={step.to}
                  variant={step.complete ? "outline" : "solid"}
                  colorScheme="blue"
                  size="md"
                  flexBasis={["100%", "auto"]}
                  marginInlineEnd={[4, 6]}
                  marginBlockEnd={[4, 0]}
                  marginInlineStart={[4, "auto"]}
                  minWidth="fit-content"
                  whiteSpace="wrap"
                  justifyContent="start"
                  textAlign="start"
                  style={{ textWrap: "balance" }}
                  paddingBlock={4}
                  height="fit-content"
                >
                  {step.complete
                    ? t("registry:confirm.review", { step: step.name })
                    : t("registry:confirm.provide", { step: step.name })}
                </Button>
              </Flex>
            </Flex>
          ))}
        </Stack>

        <FormControl>
          <FormLabel fontSize="lg">{t("registry:confirm.additionalInfoLabel")}</FormLabel>
          <InputTextArea
            name="additional_info"
            dataTestId="additional-info"
            value={additionalInfo}
            // Only saved on submit, so don't offer it before canSubmit — typing
            // here while another step is incomplete would be lost on navigation.
            disabled={isSubmitted || !canSubmit}
            placeholder={t("registry:confirm.additionalInfoPlaceholder")}
            height="8rem"
            maxLength={ADDITIONAL_INFO_MAX_LENGTH}
            onChange={setAdditionalInfoDraft}
          />
        </FormControl>

        <Text fontSize="lg">{t("registry:confirm.submitDisclaimer")}</Text>

        {isSubmitted ? (
          <Button
            data-testid="application-submitted"
            size="lg"
            colorScheme="blue"
            width="full"
            isDisabled
          >
            {t("registry:confirm.applicationSubmitted")}
          </Button>
        ) : (
          <Button
            data-testid="submit-application"
            isDisabled={!canSubmit}
            onClick={submit}
            width="full"
            colorScheme="blue"
            size="lg"
            fontSize="xl"
            paddingInline={20}
            paddingY={8}
          >
            {t("registry:confirm.submitApplication")}
          </Button>
        )}
      </VStack>
    </VStack>
  );
};
